/**
 * useRingPulseClock — the ONE jittered clock-owner for the at-rest
 * ring-pulse (AV.W14.2).
 *
 * ─── clock-owner pattern (feedback_clock_owner_pattern) ───────────────
 * Structural twins: `usePhaseTransition` (AL-W2, the ManualTimeline phase
 * clock), `useCompletionChoreography` (AN-T1, retired `useContainerMorph`),
 * `maplibre/composable` (AO-T4). This composable joins the family: ONE
 * time-source (a single self-rescheduling `setTimeout` cycle — NOT a
 * resident rAF), ONE driven channel set (the `armNonce` ref), ONE watcher
 * (the `isRunning` edge watcher). Both the dial (B0, via the renderer seam)
 * and the dock signature (B5, via `Dock.vue`) read the SAME `armNonce` —
 * there is no second clock in the dock.
 *
 * WHY one clock, not two: when two surfaces each own a pulse scheduler the
 * intervals drift and the dial + dock fall out of lockstep (the silent-cut
 * bug class the clock-owner pattern retires). Promoting the schedule to ONE
 * owner whose `armNonce` BOTH surfaces consume makes the two channels
 * structurally unable to desync.
 *
 * DDR-AV-W14-BREATH — the breathing-register doctrine. There is ONE
 * oscillator vocabulary: the dial `idleSweep` is the canon breathing
 * register (always-on, untouched); this clock is the demand-arm SCHEDULER
 * that fires the ring-pulse on top of that breath. The dock signature, the
 * survey gold sweep, and the dashboard live accents all ride THIS clock
 * family — never a new metronome, never a second resident scheduler.
 *
 * The schedule is intentionally NON-metronomic: a ~20s first arm, then
 * `25s + rand(0, 20s)` thereafter, so the instrument never reads as a
 * ticking clock. `prefers-reduced-motion: reduce` disables arming entirely
 * (no pulse, the parked dial stays stiff). The `isRunning` rising edge
 * resets the cycle so a run starts from a clean ~20s window and no pulse
 * fires DURING ramp-up (the user's "ramp-up pulse awful" redress — the
 * pulse vocabulary lives at IDLE, not during a live run).
 */

import { effectScope, onScopeDispose, ref, toRef, watch, type Ref } from "vue";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";

/** The first arm lands sooner (~20s) so a freshly-landed idle instrument
 *  acknowledges itself without a long cold wait; subsequent arms widen. */
export const RING_PULSE_FIRST_DELAY_MS = 20_000;

/** Steady-state base interval between arms. */
export const RING_PULSE_BASE_INTERVAL_MS = 25_000;

/** Random jitter band added to the base interval (0–20s). The arm interval
 *  is therefore `25s + rand(0, 20s)` ∈ [25s, 45s) — never a fixed period,
 *  so the pulse never reads as metronomic. */
export const RING_PULSE_JITTER_MS = 20_000;

/**
 * Read the live `prefers-reduced-motion: reduce` preference. The clock
 * parks on the same signal the dial breath parks on (`paintMeter`). Unlike
 * the per-frame paint reader, this is queried at most once per arm window
 * (~25s), so it resolves `matchMedia` FRESH each call rather than caching a
 * `MediaQueryList` — no per-call cost concern, and the live preference is
 * always read (a mid-session PRM toggle is honoured on the next schedule).
 * SSR / no-matchMedia → false (the scheduler simply never reads a reduced
 * preference outside the browser).
 */
function reducedMotion(): boolean {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface RingPulseClock {
    /**
     * The arm signal — a monotonically-increasing nonce. BOTH the dial and
     * the dock signature watch this ref; each increment is one pulse arm.
     * A nonce (not a boolean pulse) so a consumer can never miss an arm to
     * a coalesced reactive flush — every increment is a distinct value.
     */
    readonly armNonce: Readonly<Ref<number>>;
}

export interface RingPulseClockOptions {
    /**
     * The live `isRunning` source. The rising edge resets the cycle (clean
     * window, no ramp-up pulse); while running the clock holds (no arms).
     * A getter so the composable reads the reactive source the host owns.
     */
    isRunning: () => boolean;
    /**
     * Test seam — inject a deterministic jitter source in [0, 1). Defaults
     * to `Math.random`. The clock test pins non-metronomic intervals by
     * driving this with a known sequence.
     */
    random?: () => number;
}

/**
 * Build the ring-pulse clock over an `isRunning` source. The testable seam
 * — a spec drives `createRingPulseClock` directly with an injected
 * `isRunning` ref + a deterministic `random`, exercising the non-metronomic
 * schedule + PRM-park + rising-edge reset without touching the page-lifetime
 * singleton below.
 *
 * Must run inside a Vue effect scope (`<script setup>` / a host composable /
 * the detached `effectScope` the singleton wraps it in) so `onScopeDispose`
 * clears the pending timer on teardown.
 */
export function createRingPulseClock(
    options: RingPulseClockOptions,
): RingPulseClock {
    const { isRunning, random = Math.random } = options;
    const armNonce = ref(0);

    let timer: ReturnType<typeof setTimeout> | null = null;

    function clearTimer(): void {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    }

    /** The next arm delay: the ~20s first window, then `25s + rand(0,20s)`. */
    function nextDelay(first: boolean): number {
        if (first) return RING_PULSE_FIRST_DELAY_MS;
        return (
            RING_PULSE_BASE_INTERVAL_MS +
            Math.floor(random() * RING_PULSE_JITTER_MS)
        );
    }

    /**
     * Schedule the next arm. ONE `setTimeout` cycle — on fire it bumps the
     * nonce (the arm) and reschedules itself. NEVER a resident rAF: between
     * arms zero frames are scheduled by this owner (the demand-arm proof).
     * Parked under PRM (no arming) and while running (the pulse is an
     * idle-only vocabulary).
     */
    function schedule(first: boolean): void {
        clearTimer();
        if (reducedMotion() || isRunning()) return;
        timer = setTimeout(() => {
            timer = null;
            // Re-check at fire time: a run may have started, or PRM toggled,
            // between scheduling and firing.
            if (!reducedMotion() && !isRunning()) {
                armNonce.value += 1;
            }
            schedule(false);
        }, nextDelay(first));
    }

    // ONE watcher drives the whole fan-out. `immediate: true` runs it on
    // mount with the initial `isRunning()` value: idle → open the schedule
    // from a fresh ~20s window; running → hold (no arms). The rising edge
    // clears the pending arm (no ramp-up pulse); the falling edge re-opens
    // the idle schedule.
    watch(
        isRunning,
        (running) => {
            if (running) {
                clearTimer();
                return;
            }
            schedule(true);
        },
        { immediate: true },
    );

    onScopeDispose(clearTimer);

    return { armNonce };
}

// Module-scoped singleton — ONE ring-pulse clock for the page session.
// Lazily built on first `useRingPulseClock()` call; the watcher + timer are
// installed inside a detached `effectScope` so they survive across component
// mounts/unmounts (the dial + dock outlive any single mount) without leaking
// — the scope is page-lifetime by construction. Mirrors the
// `usePhaseTransition` singleton block (the conforming clock-owner site).
let singleton: RingPulseClock | null = null;

/**
 * Access the SHARED ring-pulse clock. BOTH the dial (B0, via
 * `useMeterRenderer`) and the dock signature (B5, `Dock.vue`) call this and
 * watch the one returned `armNonce` — ONE clock, two channels, no second
 * scheduler in the dock and no prop-drill (feedback_clock_owner_pattern).
 * Bound to the Pinia store's `isRunning` on first call.
 */
export function useRingPulseClock(): RingPulseClock {
    if (singleton) return singleton;
    const speedtest = useSpeedtestStore();
    // `toRef` re-derives a live `Ref` from the store's `isRunning`; the
    // clock's watcher tracks it for the rising/falling-edge reset.
    const isRunning: Ref<boolean> = toRef(speedtest, "isRunning");
    const scope = effectScope(true);
    // `scope.run` only returns `undefined` when the scope is already
    // inactive; this scope is freshly constructed one line up, so the run
    // always yields the clock — the non-null assertion is the honest
    // type-narrowing (no `as`-cast laundering, precept-6).
    singleton = scope.run(() =>
        createRingPulseClock({ isRunning: () => isRunning.value }),
    )!;
    return singleton;
}
